import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import {
  Phone,
  RefreshCw,
  Save,
  ShieldAlert,
  UserRound,
} from "lucide-react";
import {
  getHumanInLoopError,
  humanInLoopApi,
} from "../../api/humanInLoopApi";
import StopAssignmentPlanner, {
  buildAssignmentPlan,
  validateStopOrder,
} from "./StopAssignmentPlanner";
import "./humanInLoop.css";

const managerId = () =>
  localStorage.getItem("managerMobile") || localStorage.getItem("managerId") || "MANAGER";

const HumanInLoopTourEditor = ({ isOpen, tourId, onClose, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [editor, setEditor] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [stops, setStops] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [sequenceLock, setSequenceLock] = useState("RELATIVE");
  const sessionRef = useRef(null);
  const closedRef = useRef(false);

  const releaseSession = useCallback(async () => {
    const sessionId = sessionRef.current;
    sessionRef.current = null;
    if (!sessionId) return;
    try {
      await humanInLoopApi.release(sessionId);
    } catch (_) {
      // The server-side session TTL is the final fallback.
    }
  }, []);

  const initialise = useCallback(async () => {
    if (!isOpen || !tourId) return;
    setLoading(true);
    setError("");
    setEditor(null);
    setDrivers([]);
    setStops([]);
    setAssignments([]);
    closedRef.current = false;
    try {
      const [editorResponse, driversResponse] = await Promise.all([
        humanInLoopApi.getTourEditor(tourId),
        humanInLoopApi.getDrivers(),
      ]);
      if (closedRef.current) return;
      const data = editorResponse.data;
      const realStops = (data.stops || [])
        .filter((stop) => Number(stop.booking_id) !== 1)
        .sort((left, right) => Number(left.sequence || 0) - Number(right.sequence || 0));
      setEditor(data);
      setDrivers(driversResponse.data || []);
      setStops(realStops);
      setAssignments(buildAssignmentPlan(realStops, data.tour.vehicle_id));
      const existingLock = realStops.find((stop) => stop.sequence_lock)?.sequence_lock;
      setSequenceLock(String(existingLock || "RELATIVE").toUpperCase());
    } catch (requestError) {
      setError(getHumanInLoopError(requestError, "Unable to load the tour editor."));
    } finally {
      setLoading(false);
    }
  }, [isOpen, tourId]);

  useEffect(() => {
    void initialise();
    return () => {
      closedRef.current = true;
      void releaseSession();
    };
  }, [initialise, releaseSession]);

  const driverByVehicle = useMemo(
    () => new Map(drivers.map((driver) => [String(driver.vehicle_id), driver])),
    [drivers],
  );

  const handleSave = async () => {
    if (!editor || !stops.length || !assignments.length) return;
    const orderError = validateStopOrder(stops);
    if (orderError) {
      setError(orderError);
      return;
    }

    const sourceVehicleId = String(editor.tour.vehicle_id);
    const targetVehicleIds = assignments
      .map((assignment) => assignment.targetVehicleId)
      .filter(Boolean)
      .map(String);
    const affectedVehicleIds = [...new Set([sourceVehicleId, ...targetVehicleIds])];
    const expectedTourVersions = {};
    for (const vehicleId of affectedVehicleIds) {
      expectedTourVersions[vehicleId] = vehicleId === sourceVehicleId
        ? Number(editor.tour.tour_version || 0)
        : Number(driverByVehicle.get(vehicleId)?.tour_version || 0);
    }
    const expectedAssignmentVersions = Object.fromEntries(
      assignments.map((assignment) => [
        String(assignment.bookingId),
        Number(assignment.assignmentVersion || 0),
      ]),
    );

    setSaving(true);
    setError("");
    try {
      const sessionResponse = await humanInLoopApi.createEditSession({
        manager_id: managerId(),
        manager_name: "Operations Manager",
        vehicle_ids: affectedVehicleIds,
        booking_ids: assignments.map((assignment) => String(assignment.bookingId)),
        base_tour_versions: expectedTourVersions,
        ttl_sec: 300,
        reason: "MANUAL_TOUR_ASSIGNMENT_EDIT",
      });
      sessionRef.current = sessionResponse?.session?.session_id;
      if (!sessionRef.current) {
        throw new Error("The allocator did not return an edit session ID.");
      }

      await humanInLoopApi.editTourAssignments(tourId, {
        edit_session_id: sessionRef.current,
        source_tour_id: Number(tourId),
        expected_tour_versions: expectedTourVersions,
        expected_assignment_versions: expectedAssignmentVersions,
        booking_assignments: assignments.map((assignment) => ({
          booking_id: Number(assignment.bookingId),
          target_vehicle_id: assignment.targetVehicleId == null
            ? null
            : String(assignment.targetVehicleId),
          assignment_lock: assignment.targetVehicleId == null
            ? "NONE"
            : String(assignment.assignmentLock || "VEHICLE").toUpperCase(),
        })),
        ordered_stop_ids: stops.map((stop) => String(stop.stop_id)),
        sequence_lock: sequenceLock,
        manager_id: managerId(),
        reason_code: "MANUAL_TOUR_ASSIGNMENT_EDIT",
        reason_text: `Edited individual stop sequence and assignments for tour ${tourId}`,
      });
      sessionRef.current = null;
      onSaved?.();
      onClose?.();
    } catch (requestError) {
      await releaseSession();
      const status = requestError?.response?.status;
      setError(
        status === 409
          ? "This tour, one of its bookings, or a selected driver plan changed while you were editing. Close and reopen the editor to use the latest state."
          : getHumanInLoopError(requestError, "Unable to save the tour changes."),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleClose = async () => {
    closedRef.current = true;
    await releaseSession();
    onClose?.();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="xl" backdrop="static" scrollable>
      <ModalHeader toggle={handleClose}>Edit stop sequence and driver assignments</ModalHeader>
      <ModalBody>
        {loading && (
          <div className="hitl-centre">
            <RefreshCw className="hitl-spin" /> Loading tour…
          </div>
        )}
        {error && (
          <div className="hitl-alert">
            <ShieldAlert size={18} /> {error}
          </div>
        )}
        {!loading && editor && (
          <>
            <div className="hitl-tour-heading-card">
              <div>
                <strong>Tour #{editor.tour.id}</strong>
                <span><UserRound size={16} /> {editor.tour.driver_name || "Unnamed driver"}</span>
                <span><Phone size={16} /> {editor.tour.driver_mobile || "No phone number"}</span>
              </div>
              <div className="hitl-tour-heading-help">
                Reorder each pickup/drop stop independently, then batch-assign selected bookings to drivers.
              </div>
            </div>
            <StopAssignmentPlanner
              stops={stops}
              setStops={setStops}
              assignments={assignments}
              setAssignments={setAssignments}
              drivers={drivers}
              sequenceLock={sequenceLock}
              setSequenceLock={setSequenceLock}
              disabled={saving}
              onError={setError}
            />
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <button type="button" className="hitl-secondary-button" onClick={handleClose} disabled={saving}>
          Cancel
        </button>
        <button
          type="button"
          className="hitl-primary-button"
          onClick={handleSave}
          disabled={loading || saving || !editor || !stops.length}
        >
          {saving ? <RefreshCw className="hitl-spin" size={17} /> : <Save size={17} />}
          {saving ? "Saving…" : "Save assignments and stop sequence"}
        </button>
      </ModalFooter>
    </Modal>
  );
};

export default HumanInLoopTourEditor;
